import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardForm'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import BillPayAppRoute from 'src/navigation/appRoutes/billPay.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  billPayAllUrl,
  billPayNeedsApprovalUrl,
  createBillPayUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrgApprovalRules } from 'src/queries/organizations/cardApproverRules.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectBillPayAllMatch,
  selectBillPayNeedsApprovalMatch,
  selectCalculatedBackgroundLocation,
} from 'src/redux/selectors/router.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'

const BillPay: React.FC = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const billPayAllMatch = useSelector(selectBillPayAllMatch)
  const billPayNeedsApprovalMatch = useSelector(selectBillPayNeedsApprovalMatch)
  const user = useSelector(selectUserListModelFromState)

  const { billPayEnabled } = useSelector(selectBrandFeaturesFromState)
  const {
    VIEW_BILL_PAY_SECTION: viewBillPay,
    CREATE_BILL_PAY_CARD: canCreateBillPayCard,
  } =
    useQueryUserFeatures([
      UserFeature.VIEW_BILL_PAY_SECTION,
      UserFeature.CREATE_BILL_PAY_CARD,
    ]).data || {}
  const showBillPay = viewBillPay && billPayEnabled

  const { data } = useQueryOrgApprovalRules({ orgId: user?.organizationId })
  const userIsApprover = data?.approverId === user?.id

  const subnavOptions = useMemo(
    () => [
      {
        label: <Trns source={'All Invoices'} />,
        to: billPayAllUrl(),
        isActive: billPayAllMatch !== null,
        ariaLabelSource: 'All Invoices',
        'data-testid': 'BillPayAllInvoices',
      },
      ...(userIsApprover
        ? [
            {
              label: <Trns source={'Needs Approval'} />,
              to: billPayNeedsApprovalUrl(),
              isActive: billPayNeedsApprovalMatch !== null,
              ariaLabelSource: 'Needs Approval',
              'data-testid': 'BillPayNeedsApproval',
            },
          ]
        : []),
    ],
    [userIsApprover, billPayAllMatch, billPayNeedsApprovalMatch],
  )

  const buttons = useMemo(
    () => [
      {
        design: ButtonDesigns.designCtaInverse,
        type: ButtonTypes.button,
        text: <Trns source={'Pay Multiple Invoices'} />,
        linkProps: canCreateBillPayCard
          ? {
              to: {
                pathname: createBillPayUrl(),
                search: '',
                state: { screen: CreateVirtualCardScreen.MultipleBillPay },
                hash: '',
              },
              ariaLabelSource: 'Pay Multiple Invoices',
            }
          : {
              to: actionUnavailableUrl(),
              ariaLabel: 'Pay Multiple Invoices',
            },
      },
      {
        design: ButtonDesigns.designCta,
        type: ButtonTypes.button,
        text: <Trns source={'Pay Invoice'} />,
        linkProps: {
          to: canCreateBillPayCard
            ? createBillPayUrl()
            : actionUnavailableUrl(),
          ariaLabel: 'Pay Invoice',
        },
      },
    ],
    [canCreateBillPayCard],
  )

  const isLoading = viewBillPay === undefined

  return isLoading ? (
      <LoadingBackgroundPage />
    ) : showBillPay ? (
          <LayoutShell
            subnavDesign={SubnavDesign.Floating}
            subnavOptions={subnavOptions}
            content={
              <Switch location={calculatedBackgroundLocation}>
                {BillPayAppRoute.subRoutes.map((route, i) => (
                  <Route key={i} {...route.routeProps}>
                    {route.component}
                  </Route>
                ))}
                <Route {...BillPayAppRoute.routeProps} exact>
                  <Rdrct to={billPayAllUrl()} />
                </Route>
                <Route path={'/'}>
                  <NotFoundBackgroundPageContent />
                </Route>
              </Switch>
            }
            title={<Trns source={'Bill Pay Cards'} />}
            buttons={buttons}
          />
        ) : (
          <NotFoundBackgroundPage />
        )}

export default BillPay
